import { useState, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { createQueryClient } from '@/lib/query/client';
import { AuthProvider } from '@/lib/auth/AuthProvider';
import { ToastProvider } from '@/lib/ui';
import { TooltipProvider } from '@/lib/ui/Tooltip';
import i18n from '@/lib/i18n/config';

/**
 * Single composition root for all app-wide context (plan §3).
 *
 * Context only. Nothing that RENDERS UI of its own belongs here, because
 * everything in this file sits ABOVE `<BrowserRouter>` and above the app's error
 * boundary: a child mounted here has no router context to read and no boundary to
 * catch it if it throws, so one bad render blanks `#root` instead of showing the
 * error card. That is exactly what `MediaPickerHost` did (F-28) — it lives in
 * `App` now, inside the router.
 */
export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient);
  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <TooltipProvider>
            <AuthProvider>{children}</AuthProvider>
          </TooltipProvider>
        </ToastProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
}
